"use client"

import { useCallback } from "react"
import { useLocale, useTranslations } from "next-intl"
import { getServerActionError, withQueryHandling, type WithQueryHandlingOptions } from "@/lib/with-query-handling"
import { useTranslatedErrorMessage } from "@/hooks/use-translated-error-message"

/** Translate action presentation while retaining statuses, field paths and server contracts. */
export function useTranslatedQueryHandling() {
  const locale = useLocale()
  const common = useTranslations("Common")
  const { message: localizeMessage, field: localizeField } = useTranslatedErrorMessage()

  return useCallback(function handle<T>(promise: Promise<T>, options: WithQueryHandlingOptions<T> = {}): Promise<T> {
    const localized = promise.catch((error: unknown) => {
      if (locale === "en") throw error
      const message = error instanceof Error ? localizeMessage(error.message) : common("unknownError")
      throw new Error(message, { cause: error })
    }).then(result => {
      const actionError = getServerActionError(result)
      if (!actionError) return result
      const validationErrors = actionError.validationErrors
      let localizedFields: typeof validationErrors
      if (Array.isArray(validationErrors)) {
        localizedFields = validationErrors.map(issue => ({ ...issue, message: localizeField(issue.message) }))
      } else if (validationErrors) {
        localizedFields = Object.fromEntries(Object.entries(validationErrors).map(([field, messages]) => [
          field, Array.isArray(messages) ? messages.map(localizeField) : localizeField(messages),
        ]))
      }
      return Object.assign({}, result, {
        errorMessage: localizeMessage(actionError.errorMessage ?? actionError.error ?? common("unknownError")),
        ...(localizedFields ? { validationErrors: localizedFields } : {}),
      })
    })
    return withQueryHandling(localized, {
      loading: common("loading"), success: common("completed"),
      ...options,
    })
  }, [common, locale, localizeField, localizeMessage])
}
